import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Helmet } from 'react-helmet-async';
import { useApplicantData } from '../../hooks/useApplicantData';
import { getApplicantData } from '../../util/helpers/applicantDataHelper';
import { PAGES } from '../../constants/pages';
import useApplicationOwnerAddress from '../../hooks/useApplicationOwnerAddress';
import { APPLICANT_TYPES, KYC_DOCUMENT_TYPES } from '../../util/constants';
import KycLink from './KycLink';
import BaseLayout from '../../components/BaseLayout';
import Header from '../../components/Header';
import KycSelectionContainer from '../../components/KycSelectionContainer';
import useKycProofOfResidencyDocuments from '../../hooks/useKycProofOfResidencyDocuments';
import { selectRequiredDocuments } from '../../store/selectors/documentsSelectors';
import {
  retrieveFromSessionStorage,
  storeInSessionStorage,
} from '../../util/helpers/sessionStorageHelper';
import {
  ACCOUNT_TYPE_GTM,
  KYC_SELECTED_OPTION,
} from '../../constants/sessionStorage';
import { selectSettings } from '../../store/selectors/settingsSelectors';
import useStepPost from '../../hooks/useStepPost';
import { gtmStepHelper } from '../../util/helpers/gtmHelper';
import { ACCOUNT_STEP_COMPLETE, ACCOUNT_STEP_VIEW } from '../../util/gtmEvents';
import useGtmStepHook from '../../util/hooks/useGtmStepHook';
import { CONTENT_TYPE } from '../../constants/localStorage';

const Wrapper = styled.div`
  padding: 50px 0;
  display: flex;
  flex-direction: column;

  @media only screen and (max-width: 500px) {
    padding: 0;
  }
`;

const KycProofOfResidency = ({ applicantType }) => {
  const { t } = useTranslation();
  const { onSuccess: onSuccessSkip } = useStepPost({
    nextPath: PAGES.KYC_DONE_SKIP,
  });
  const ApplicantData = useApplicantData(applicantType);
  const applicantAddress = useApplicationOwnerAddress(APPLICANT_TYPES.MAIN);
  const requiredDocuments = useSelector(selectRequiredDocuments);
  const { kycResidencyOptions } = useKycProofOfResidencyDocuments(
    requiredDocuments,
    applicantType,
  );
  const options = kycResidencyOptions();
  const isUtilityBill =
    options?.filter(
      (option) => KYC_DOCUMENT_TYPES[option.DocumentType] === 'UtilityBill',
    ).length > 0;
  const { BrokerName } = useSelector(selectSettings);

  const { gtmStepEvent } = useGtmStepHook();
  const accountGtmType = retrieveFromSessionStorage(ACCOUNT_TYPE_GTM);

  useEffect(() => {
    storeInSessionStorage(CONTENT_TYPE, 'multipart/form-data');
  }, []);

  useEffect(() => {
    gtmStepHelper({
      gtmStepEvent,
      eventName: ACCOUNT_STEP_VIEW,
      stepLabel: 'Kyc Proof of Residency',
      accountType: accountGtmType,
      accountName: ApplicantData?.PersonName,
      accountDateOfBirth: ApplicantData?.PersonBirthday,
      accountPhone: ApplicantData?.Phone,
      accountAddress: ApplicantData?.PersonAddress,
      customParams: {
        timestamp: new Date().toISOString(),
      },
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGtmEvent = () => {
    gtmStepHelper({
      gtmStepEvent,
      eventName: ACCOUNT_STEP_COMPLETE,
      stepLabel: 'Kyc Proof of Residency',
      accountType: accountGtmType,
      accountName: ApplicantData?.PersonName,
      accountDateOfBirth: ApplicantData?.PersonBirthday,
      accountPhone: ApplicantData?.Phone,
      accountAddress: ApplicantData?.PersonAddress,
      customParams: {
        timestamp: new Date().toISOString(),
      },
    });
  };

  const onClick = (option) => {
    handleGtmEvent();
    storeInSessionStorage(KYC_SELECTED_OPTION, option);
  };

  return (
    <BaseLayout
      previousPath={PAGES.KYC_VERIFICATION}
      isKycProcess="true"
      noLogoBox
    >
      <Helmet>
        <title>
          {t('kycVerification.titleTagproofOfResidency', {
            brokerName: BrokerName,
          })}
        </title>
      </Helmet>
      <Wrapper>
        <Header
          title={t('kycVerification.proofOfResidencyNamed', {
            name: getApplicantData(ApplicantData, 'PersonName', 'FirstName'),
            address: applicantAddress,
          })}
          subtitle={
            isUtilityBill
              ? t('kycVerification.proofOfResidenceForDesc')
              : t('kycVerification.proofOfResidenceForDescWithoutUtilityBill')
          }
          subTitleMb={24}
        />
        {options?.map((option) => (
          <KycSelectionContainer
            key={option.path}
            {...option}
            onClick={onClick}
          />
        ))}
        <KycLink
          onClick={() => {
            handleGtmEvent();
            onSuccessSkip();
          }}
        />
      </Wrapper>
    </BaseLayout>
  );
};

KycProofOfResidency.propTypes = {
  applicantType: PropTypes.string,
};

export default KycProofOfResidency;
